import {useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import clsx from 'clsx'
import {KTIcon} from '../../../_metronic/helpers'
import {formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {chargeInvoice} from '../core/_request'
import {creditCardResponse} from '../core/_models'
import {getCreditCards} from './invoiceHistoryList/core/_requests'
import {LoadingSpinner} from '../../../utils/loading/loadingSpinner'

const bandeiras: Record<string, string> = {
  visa: '/media/svg/card-logos/visa.svg',
  mastercard: '/media/svg/card-logos/mastercard.svg',
  elo: '/media/svg/card-logos/elo.svg',
  amex: '/media/svg/card-logos/american-express.svg',
  hipercard: '/media/svg/card-logos/hipercard.svg',
  hiper: '/media/svg/card-logos/hiper.svg',
  diners: '/media/svg/card-logos/diners.svg',
  discover: '/media/svg/card-logos/discover.svg',
  jcb: '/media/svg/card-logos/jcb.svg',
  maestro: '/media/svg/card-logos/maestro.svg',
  mir: '/media/svg/card-logos/mir.svg',
  paypal: '/media/svg/card-logos/paypal.svg',
  unionpay: '/media/svg/card-logos/unionpay.svg',
  generic: '/media/svg/card-logos/generic.svg',
}

type Props = {
  franchiseId: number
  invoiceId: number
  referenceCode: string
  totalAmount: number
  canListCreditCards: boolean
  onClose: () => void
  onSuccess: () => void
}

const ChargeInvoiceModal = ({
  franchiseId,
  invoiceId,
  referenceCode,
  totalAmount,
  canListCreditCards,
  onClose,
  onSuccess,
}: Props) => {
  const [creditCards, setCreditCards] = useState<creditCardResponse[]>([])
  const [selectedCardId, setSelectedCardId] = useState<number>()
  const [isLoadingCards, setIsLoadingCards] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!canListCreditCards) return

    const loadCreditCards = async () => {
      try {
        setIsLoadingCards(true)
        const cards = await getCreditCards(franchiseId)
        setCreditCards(cards)
        const defaultCard = cards.find((card) => card.isFavorite > 0) ?? cards[0]
        setSelectedCardId(defaultCard?.id)
      } catch (error: any) {
        await Swal.fire({
          icon: 'warning',
          title: 'Oops...',
          text: error?.response?.data?.message,
        })
      } finally {
        setIsLoadingCards(false)
      }
    }
    loadCreditCards()
  }, [])

  const hasNoCards = canListCreditCards && !isLoadingCards && creditCards.length === 0

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      await chargeInvoice(franchiseId, invoiceId, canListCreditCards ? selectedCardId : undefined)
      await Swal.fire({
        icon: 'success',
        title: 'Cobrança aprovada',
        text: `A fatura ${referenceCode} foi paga com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      })
      onSuccess()
    } catch (error: any) {
      await Swal.fire({
        icon: 'warning',
        title: 'Cobrança não realizada',
        text: error?.response?.data?.message,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className='modal fade show d-block' role='dialog' tabIndex={-1} aria-modal='true'>
        <div className='modal-dialog modal-dialog-centered mw-500px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder m-0'>Cobrar no cartão</h2>
              <div
                className='btn btn-icon btn-sm btn-active-icon-danger'
                onClick={() => !isSubmitting && onClose()}
                style={{cursor: 'pointer'}}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>

            <div className='modal-body p-9'>
              <div className='mb-7 text-gray-700'>
                Fatura <span className='fw-bolder'>{referenceCode}</span> no valor de{' '}
                <span className='fw-bolder'>{formatMonetaryToBr(totalAmount)}</span>.
              </div>

              {!canListCreditCards && (
                <div className='alert alert-light-primary mb-7'>
                  Será cobrado o cartão padrão da franquia.
                </div>
              )}

              {canListCreditCards && isLoadingCards && (
                <LoadingSpinner
                  title='Carregando cartões'
                  style={{
                    width: '2rem',
                    height: '2rem',
                    colorSpinner: '#f1416c',
                    colorText: '#f1416c',
                  }}
                />
              )}

              {hasNoCards && (
                <div className='alert alert-light-warning mb-7'>
                  Nenhum cartão cadastrado para esta franquia. Cadastre um cartão na aba "Meus
                  Cartões" para realizar a cobrança.
                </div>
              )}

              {canListCreditCards && !isLoadingCards && creditCards.length > 0 && (
                <div className='mb-7'>
                  <label className='required fw-bold fs-6 mb-3'>Selecione o cartão</label>
                  {creditCards.map((card) => (
                    <label
                      key={card.id}
                      className={clsx(
                        'd-flex align-items-center border border-dashed rounded p-4 mb-3 cursor-pointer',
                        selectedCardId === card.id
                          ? 'border-primary bg-light-primary'
                          : 'border-gray-300'
                      )}
                    >
                      <input
                        type='radio'
                        className='form-check-input me-4'
                        name='credit_card_id'
                        checked={selectedCardId === card.id}
                        onChange={() => setSelectedCardId(card.id)}
                        disabled={isSubmitting}
                      />
                      <img
                        src={bandeiras[card?.brand?.description?.toLowerCase()] || ''}
                        alt={card?.brand?.description}
                        className='me-4'
                        style={{height: '1.75rem'}}
                        onError={(e) => {
                          e.currentTarget.src = bandeiras['generic']
                        }}
                      />
                      <div className='d-flex flex-column'>
                        <span className='fw-bolder text-gray-800'>
                          {card?.brand?.description} **** {card.lastNumbers}
                          {card.isFavorite > 0 && (
                            <span className='badge badge-light-success ms-2'>Favorito</span>
                          )}
                        </span>
                        <span className='text-muted fs-7'>
                          {card.holderName} · {`${card.expirationMonth}/${card.expirationYear}`}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <div className='d-flex justify-content-end'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    isLoadingCards ||
                    hasNoCards ||
                    (canListCreditCards && !selectedCardId)
                  }
                >
                  {isSubmitting ? (
                    <span className='indicator-progress d-block'>
                      Aguarde...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : (
                    'Confirmar cobrança'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {ChargeInvoiceModal}
